// src/pages/JavascriptPage.js
import React from 'react';
import CodePlayground from '../components/CodePlayground';
import Quiz from '../components/Quiz';
import CopyButton from '../components/CopyButton';

function JavascriptPage() {
  return (
    <div>
      {/* Hero Section */}
      <header className="hero-section text-center" role="banner">
        <div className="container">
          <h1>JavaScript</h1>
          <p className="lead">Add interactivity to your sites</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5" role="main">
        <section id="js-content" aria-labelledby="js-heading">
          <h2 id="js-heading">Learn JavaScript Basics</h2>
          <p>JavaScript makes your web pages interactive. Let’s explore the core concepts for beginners!</p>

          {/* Chunk 1: Adding JavaScript */}
          <div className="chunk">
            <h3>1. Adding JavaScript</h3>
            <p>Add JavaScript using a <code>&lt;script&gt;</code> tag in your HTML, either inline or via an external file.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Inline -->
<script>
  alert("Hello, World!");
</script>

<!-- External -->
<script src="script.js"></script>`}
              </pre>
              <CopyButton text={`<!-- Inline -->
<script>
  alert("Hello, World!");
</script>

<!-- External -->
<script src="script.js"></script>`} />
            </div>
            <p><strong>Steps:</strong></p>
            <ol>
              <li>Add the script tag before the closing <code>&lt;/body&gt;</code> tag for better performance.</li>
              <li>For external files, create a <code>.js</code> file and link it.</li>
            </ol>
            <div className="tip">
              <strong>VS Code Tip:</strong> Type <code>script</code> and press <strong>Tab</strong> to auto-generate a script tag.
            </div>
          </div>

          {/* Chunk 2: Variables */}
          <div className="chunk">
            <h3>2. Variables</h3>
            <p>Store data using <code>let</code>, <code>const</code>, or <code>var</code> (use <code>let</code> and <code>const</code> for modern code).</p>
            <div className="code-snippet">
              <pre>
                {`let name = "Alice"; // Can change
const age = 20; // Cannot change
console.log(name, age); // Outputs: Alice 20`}
              </pre>
              <CopyButton text={`let name = "Alice"; // Can change
const age = 20; // Cannot change
console.log(name, age); // Outputs: Alice 20`} />
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + Shift + J</strong> (Mac) or <strong>Ctrl + Shift + J</strong> (Windows) to open the browser console and see <code>console.log</code> output.
            </div>
          </div>

          {/* Chunk 3: Functions */}
          <div className="chunk">
            <h3>3. Functions</h3>
            <p>Functions are reusable blocks of code. Define them with <code>function</code>.</p>
            <div className="code-snippet">
              <pre>
                {`function sayHello(name) {
  return "Hello, " + name + "!";
}
console.log(sayHello("Bob")); // Outputs: Hello, Bob!`}
              </pre>
              <CopyButton text={`function sayHello(name) {
  return "Hello, " + name + "!";
}
console.log(sayHello("Bob")); // Outputs: Hello, Bob!`} />
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Cmd + D</strong> (Mac) or <strong>Ctrl + D</strong> (Windows) to select and edit multiple instances of a function name.
            </div>
          </div>

          {/* Chunk 4: Conditionals */}
          <div className="chunk">
            <h3>4. Conditionals</h3>
            <p>Use <code>if</code>, <code>else if</code>, and <code>else</code> to make decisions.</p>
            <div className="code-snippet">
              <pre>
                {`let age = 18;
if (age >= 18) {
  console.log("You can vote!");
} else {
  console.log("Too young to vote.");
}`}
              </pre>
              <CopyButton text={`let age = 18;
if (age >= 18) {
  console.log("You can vote!");
} else {
  console.log("Too young to vote.");
}`} />
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + /</strong> (Mac) or <strong>Ctrl + /</strong> (Windows) to comment out conditionals while testing.
            </div>
          </div>

          {/* Chunk 5: Loops */}
          <div className="chunk">
            <h3>5. Loops</h3>
            <p>Repeat tasks with <code>for</code> or <code>while</code> loops.</p>
            <div className="code-snippet">
              <pre>
                {`for (let i = 1; i <= 3; i++) {
  console.log("Count: " + i);
}
// Outputs:
// Count: 1
// Count: 2
// Count: 3`}
              </pre>
              <CopyButton text={`for (let i = 1; i <= 3; i++) {
  console.log("Count: " + i);
}
// Outputs:
// Count: 1
// Count: 2
// Count: 3`} />
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Option + Up/Down</strong> (Mac) or <strong>Alt + Up/Down</strong> (Windows) to move loop lines up or down.
            </div>
          </div>

          {/* Chunk 6: DOM Manipulation & Playground */}
          <div className="chunk">
            <h3>6. DOM Manipulation & Playground</h3>
            <p>Change your webpage with JavaScript using the Document Object Model (DOM).</p>
            <CodePlayground initialCode={`<h1 id="title">Hello</h1>
<button onclick="changeText()">Change Text</button>

<script>
function changeText() {
  document.getElementById("title").innerText = "Text Changed!";
  document.getElementById("title").style.color = "blue";
}
</script>`} />
          </div>

          {/* Chunk 7: Events */}
          <div className="chunk">
            <h3>7. Events</h3>
            <p>Respond to user actions like clicks with event listeners.</p>
            <div className="code-snippet">
              <pre>
                {`<button onclick="alert('Clicked!')">Click Me</button>

<script>
  document.getElementById("myButton").addEventListener("click", function() {
    alert("Button clicked!");
  });
</script>`}
              </pre>
              <CopyButton text={`<button onclick="alert('Clicked!')">Click Me</button>

<script>
  document.getElementById("myButton").addEventListener("click", function() {
    alert("Button clicked!");
  });
</script>`} />
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + T</strong> (Mac) or <strong>Ctrl + T</strong> (Windows) to quickly find event listener functions in your code.
            </div>
          </div>
        </section>

        {/* Quiz Section */}
        <div className="mt-5">
          <h3>Test Your Knowledge</h3>
          <Quiz 
            question="Which keyword is used to declare a variable that CANNOT be changed?"
            options={["let", "var", "const", "static"]}
            correctAnswerIndex={2}
          />
        </div>
      </main>
    </div>
  );
}

export default JavascriptPage;